/* SUBTERRA shared constants. Everything here is used identically by the server (authoritative
   simulation) and the browser (renderer), so terrain generated on both sides matches pixel for pixel. */

export const W = 640;            // world width in pixels
export const SURF = 96;          // sky rows above the nominal surface line
export const CH = 128;           // rows per terrain chunk
export const M_PER_PX = 0.5;     // metres per pixel
export const MAX_DEPTH_M = 10000;
export const MAX_DEPTH_PX = MAX_DEPTH_M / M_PER_PX;
export const WORLD_H = SURF + MAX_DEPTH_PX + 40;
export const X0 = W >> 1;        // shaft head x
export const TICK_HZ = 10;
export const TICK_DT = 1 / TICK_HZ;
export const PROTOCOL_VERSION = 3;

export type Hazard = 'none' | 'loose' | 'seepage' | 'voids' | 'rockfall' | 'heat' | 'static' | 'unstable' | 'inferno';

export interface LayerDef {
  name: string; atM: number; c: [string, string]; accent: string;
  hard: number; tier: number; hazard: Hazard; hazardLabel: string;
  /* relative ore abundance in this layer, by ore id */
  ores: Partial<Record<number, number>>;
}

/* Materials. 0 = sky, 1..9 = layer rock, 16+ = ores, 32+ = special formations. */
export const MAT_SKY = 0;
export const ORE_BASE = 16;
export const ORES = [
  { id: 16, key: 'copper', name: 'COPPER', col: '#b5703f', hi: '#d9925a', value: 3, hard: 1.1 },
  { id: 17, key: 'iron', name: 'IRON', col: '#7d4f42', hi: '#a46b58', value: 2, hard: 1.25 },
  { id: 18, key: 'silver', name: 'SILVER', col: '#a9adb2', hi: '#d8dce0', value: 6, hard: 1.2 },
  { id: 19, key: 'cobalt', name: 'COBALT', col: '#4f6f8f', hi: '#7898b6', value: 9, hard: 1.4 },
  { id: 20, key: 'gold', name: 'GOLD', col: '#c9a23c', hi: '#ecd27a', value: 14, hard: 1.1 },
  { id: 21, key: 'quartz', name: 'QUARTZ CRYSTAL', col: '#8fc7c4', hi: '#d6f2ee', value: 18, hard: 1.6 },
  { id: 22, key: 'corite', name: 'CORITE', col: '#d0563a', hi: '#ff9a5e', value: 40, hard: 1.8 },
] as const;
export const ORE_BY_ID: Record<number, (typeof ORES)[number]> = Object.fromEntries(ORES.map(o => [o.id, o]));
export const MAT_CAVE = 32;      // natural cavity wall rubble (soft)
export const MAT_GEODE = 33;
export const MAT_FOSSIL = 34;
export const MAT_RELIC = 35;
export const MAT_METEOR = 36;
export const MAT_AQUIFER = 37;

export const LAYERS: LayerDef[] = [
  { name: 'DRY SAND', atM: 0, c: ['#8d7c5d', '#857455'], accent: '#a8966f', hard: 1.0, tier: 1, hazard: 'loose', hazardLabel: 'loose fill: +30% spoil', ores: { 16: 3, 17: 1 } },
  { name: 'PACKED EARTH', atM: 100, c: ['#6c5945', '#64523f'], accent: '#7f6a52', hard: 1.25, tier: 1, hazard: 'none', hazardLabel: 'stable', ores: { 16: 3, 17: 3 } },
  { name: 'CLAY', atM: 350, c: ['#7a4a37', '#714433'], accent: '#8c5a44', hard: 1.55, tier: 1, hazard: 'seepage', hazardLabel: 'water seepage: units 10% slower', ores: { 16: 2, 17: 4, 18: 1 } },
  { name: 'LIMESTONE', atM: 800, c: ['#8a877b', '#807d71'], accent: '#a3a091', hard: 2.0, tier: 1, hazard: 'voids', hazardLabel: 'karst voids: +10% battery drain', ores: { 17: 3, 18: 2, 20: .6 } },
  { name: 'GRANITE', atM: 1500, c: ['#5e5a60', '#555158'], accent: '#a08a8e', hard: 2.6, tier: 2, hazard: 'rockfall', hazardLabel: 'rockfall at faces', ores: { 17: 2, 18: 2, 19: 1.2, 20: 1 } },
  { name: 'BASALT', atM: 2800, c: ['#323438', '#2b2d31'], accent: '#4a4d52', hard: 3.3, tier: 3, hazard: 'heat', hazardLabel: 'geothermal heat: +40% battery drain', ores: { 19: 2.5, 20: 1.2, 18: 1 } },
  { name: 'CRYSTAL DEPOSITS', atM: 4500, c: ['#2a2f3b', '#252a35'], accent: '#5b8a9a', hard: 3.8, tier: 4, hazard: 'static', hazardLabel: 'static discharge stuns units', ores: { 21: 4, 19: 1.5, 20: 1 } },
  { name: 'ANCIENT STRATA', atM: 6500, c: ['#4a3f30', '#41372a'], accent: '#6b5a40', hard: 4.4, tier: 5, hazard: 'unstable', hazardLabel: 'unstable strata: rockfall, +30% drain', ores: { 20: 2, 21: 2, 22: .6 } },
  { name: 'UNKNOWN CORE', atM: 8500, c: ['#3a1b17', '#321613'], accent: '#8f3a22', hard: 5.2, tier: 6, hazard: 'inferno', hazardLabel: 'extreme heat: +80% drain, units slower', ores: { 22: 3, 21: 1, 20: 1 } },
];
export const layerAtM = (m: number) => { let i = 0; while (i + 1 < LAYERS.length && m >= LAYERS[i + 1].atM) i++; return i; };

export const HAZARD = {
  none: { drain: 1, speed: 1, rockfall: 0, stun: 0, spoil: 1 },
  loose: { drain: 1, speed: 1, rockfall: 0, stun: 0, spoil: 1.3 },
  seepage: { drain: 1, speed: .9, rockfall: 0, stun: 0, spoil: 1 },
  voids: { drain: 1.1, speed: 1, rockfall: 0, stun: 0, spoil: 1 },
  rockfall: { drain: 1.05, speed: 1, rockfall: .003, stun: 0, spoil: 1 },
  heat: { drain: 1.4, speed: 1, rockfall: .002, stun: 0, spoil: 1 },
  static: { drain: 1.2, speed: 1, rockfall: .002, stun: .0015, spoil: 1 },
  unstable: { drain: 1.3, speed: .95, rockfall: .01, stun: .0008, spoil: 1 },
  inferno: { drain: 1.8, speed: .85, rockfall: .006, stun: .001, spoil: 1 },
} as const;

/* ---------- robots ---------- */
export type RobotClass = 'digger' | 'hauler' | 'scout' | 'engineer' | 'driller';
export const CLASSES: RobotClass[] = ['digger', 'hauler', 'scout', 'engineer', 'driller'];
export const CLASS_DEF: Record<RobotClass, { label: string; code: number; speed: number; dig: number; hardDig: number; battery: number; drain: number; cargo: number; col: string }> = {
  digger: { label: 'DIGGER', code: 0, speed: 16, dig: 1, hardDig: .6, battery: 160, drain: 1, cargo: 0, col: '#c8913a' },
  hauler: { label: 'HAULER', code: 1, speed: 18, dig: 0, hardDig: 0, battery: 150, drain: .9, cargo: 2, col: '#9a9c94' },
  scout: { label: 'SCOUT', code: 2, speed: 25, dig: 0, hardDig: 0, battery: 120, drain: .7, cargo: 0, col: '#6f9c9a' },
  engineer: { label: 'ENGINEER', code: 3, speed: 15, dig: 0, hardDig: 0, battery: 170, drain: 1, cargo: 0, col: '#d9d4c7' },
  driller: { label: 'DRILLER', code: 4, speed: 12, dig: 1, hardDig: 1.9, battery: 220, drain: 1.4, cargo: 0, col: '#b8693f' },
};
export const HARD_ROCK = 2.4; // hardness above which diggers slow down and drillers excel

export type RState = 'idle' | 'select' | 'navigate' | 'work' | 'return' | 'recharge' | 'stunned';
export const RSTATES: RState[] = ['idle', 'select', 'navigate', 'work', 'return', 'recharge', 'stunned'];

/* ---------- chambers ---------- */
export type ChamberType = 'command' | 'charging' | 'storage' | 'processing' | 'research' | 'power' | 'assembly' | 'stope';
export const CHAMBER_TYPES: ChamberType[] = ['command', 'charging', 'storage', 'processing', 'research', 'power', 'assembly', 'stope'];
export const CHAMBER_DEF: Record<ChamberType, { label: string; col: string; r: number; cost: number; work: number; desc: string }> = {
  command: { label: 'COMMAND CENTER', col: '#c8913a', r: 0, cost: 0, work: 0, desc: 'surface HQ: 3 docks, basic processing and research, 40 kW' },
  charging: { label: 'CHARGING BAY', col: '#7fa37a', r: 6, cost: 40, work: 30, desc: '+4 charging docks near the work' },
  storage: { label: 'MINERAL STORAGE', col: '#b8693f', r: 6.5, cost: 30, work: 25, desc: 'ore drop-off point, +600 capacity' },
  processing: { label: 'PROCESSING FACILITY', col: '#c9a23c', r: 7, cost: 60, work: 45, desc: 'refines 20 ore/min into materials and crushes spoil on site' },
  research: { label: 'RESEARCH LAB', col: '#6f9c9a', r: 6.5, cost: 80, work: 50, desc: 'turns materials into research (+0.04 RP/s)' },
  power: { label: 'POWER STATION', col: '#d0563a', r: 6.5, cost: 70, work: 45, desc: '+90 kW to the grid' },
  assembly: { label: 'ROBOT ASSEMBLY BAY', col: '#d9d4c7', r: 7.5, cost: 90, work: 60, desc: 'builds new units 3x faster' },
  stope: { label: 'ORE STOPE', col: '#8a877b', r: 6, cost: 0, work: 0, desc: 'excavated ore body' },
};
export const CHAMBER_CODE: Record<string, number> = Object.fromEntries(CHAMBER_TYPES.map((t, i) => [t, i + 1]));

/* ---------- skills (Core Intelligence) ---------- */
export type SkillId = 'excav' | 'path' | 'battery' | 'detect' | 'logistics' | 'deep' | 'struct' | 'parallel' | 'expand';
export const SKILLS: { id: SkillId; name: string; desc: string; effect: string }[] = [
  { id: 'excav', name: 'EXCAVATION EFFICIENCY', desc: 'tuned cutting heads', effect: 'dig time -25%, drill tier +1' },
  { id: 'battery', name: 'BATTERY OPTIMIZATION', desc: 'regenerative drive trains', effect: 'drain -35%, charging +40%' },
  { id: 'logistics', name: 'AUTOMATED LOGISTICS', desc: 'relay depots and twin bins', effect: 'hauler cargo x2, relay depots' },
  { id: 'detect', name: 'RESOURCE DETECTION', desc: 'seismic survey arrays', effect: 'survey radius x2, ore yield +25%' },
  { id: 'path', name: 'ADVANCED PATHFINDING', desc: 'network-wide route planning', effect: 'speed +20%, cross-cut tunnels' },
  { id: 'deep', name: 'DEEP DRILLING', desc: 'heavy rotary drill strings', effect: 'drill tier +2, lift shaft x1.7 speed' },
  { id: 'struct', name: 'STRUCTURAL ENGINEERING', desc: 'shoring and pre-cast segments', effect: 'build +50%, rockfall -60%, tier +1' },
  { id: 'parallel', name: 'PARALLEL OPERATIONS', desc: 'multi-face scheduling', effect: 'about 20% more dig faces, idle units re-task faster' },
  { id: 'expand', name: 'AUTONOMOUS EXPANSION', desc: 'self-replicating assembly', effect: 'unit cost -40%, fleet cap +25%, tier +1' },
];
export const SKILL_COST = [60, 160, 320, 560, 900, 1400, 2100, 3000, 4200];

/* ---------- surface layout ---------- */
export const CC_X = X0 - 46;        // command center building centre
export const HEAP_X0 = X0 + 34;     // tailings heap starts right of the headframe
export const HEAP_W = 120;
